import React, { useCallback, useEffect, useState } from "react";
import { motion } from "framer-motion";
import { getCatalogueByCategory } from "../services/catalogueService";
import { isSupabaseConfigured } from "../supabase/client";
import CatalogueModal from "../components/CatalogueModal";
import "./Sourcing.css";

const Sourcing = () => {
  // Catalogue images for every category, fetched once. Opening a category then
  // needs no further request. Empty object until loaded (or if unconfigured),
  // which the modal treats as "no images yet".
  const [catalogue, setCatalogue] = useState({});
  const [openCategory, setOpenCategory] = useState(null);

  useEffect(() => {
    if (!isSupabaseConfigured) return;
    getCatalogueByCategory()
      .then(setCatalogue)
      .catch(() => {/* fallback message covers this */});
  }, []);

  const closeModal = useCallback(() => setOpenCategory(null), []);

  // ─── Categories Data ───
  const categories = [
    { slug: "denim-wear", title: "Denim Wear", image: "/img/Sourcing/denim-wear.jpg", desc: "Five-pocket classics through to fashion fits, with laundry and finishing completed in house. In-house washing allows a shade to be held consistent across a full production run." },
    { slug: "knit-wear", title: "Knit Wear", image: "/img/Sourcing/knit-wear.jpg", desc: "Everyday essentials through to fashion knits, built for programs that repeat season after season. Because consistency between repeat orders is the priority, the same yarn and the same supplier remain assigned to the style." },
    { slug: "jackets", title: "Jackets", image: "/img/Sourcing/jackets.jpg", desc: "Quilted and padded outerwear, produced on a line equipped for complex construction. As our most demanding product category, it is served by a dedicated team and dedicated machinery." },
    { slug: "home-textiles", title: "Home Textiles", image: "/img/Sourcing/home-textiles.jpg", desc: "Bedding, towels, and living textiles from Pakistan's leading suppliers — a category of particular national strength. Orders are managed on the same quality and reporting terms as our garment programs." },
    { slug: "raw-fabrics", title: "Raw Fabrics", image: "/img/Sourcing/raw-fabrics.jpg", desc: "Denim and woven fabrics supplied to garment manufacturers worldwide. Where a buyer has manufacturing in place and requires fabric alone, we supply on that basis." },
    { slug: "workwear", title: "Workwear", image: "/img/Sourcing/workwear.jpg", desc: "Durable constructions and safety-first specifications, produced to the standards the end use demands. Where a garment must meet a compliance requirement, that compliance is documented." }
  ];

  // ─── Framer Motion Variants ───
  const fadeUp = {
    hidden: { opacity: 0, y: 40 },
    visible: { opacity: 1, y: 0, transition: { duration: 0.8, ease: "easeOut" } }
  };

  const staggerContainer = {
    hidden: { opacity: 0 },
    visible: { opacity: 1, transition: { staggerChildren: 0.15 } }
  };

  return (
    <div className="sourcing-modern-page">

      {/* ── Visual Banner Hero (Short but rich) ── */}
      <section
        className="banner-hero"
        style={{ backgroundImage: `url('/img/Sourcing/Elleti-garment-making-Laundry-Dyeing.jpeg')` }}
      >
        <div className="banner-overlay"></div>
        <div className="container banner-content">
          <motion.div initial="hidden" animate="visible" variants={fadeUp}>
            <span className="copper-eyebrow">Sourcing &amp; Merchandising</span>
            <h1 className="hero-title-bold">
              The Right Supplier for <span className="hero-italic">Every Order.</span>
            </h1>
          </motion.div>
        </div>
      </section>

      {/* ── Sticky Editorial Overview ── */}
      <section className="sourcing-engine-section">
        <div className="container engine-grid">
          <div className="engine-sticky-col">
            <motion.div initial="hidden" whileInView="visible" viewport={{ once: true }} variants={fadeUp}>
              <h2>The Sourcing Engine.</h2>
              <div className="copper-divider"></div>
              <p className="engine-lead">
                We match every order to the supplier best suited to it, chosen from a network we have worked with for years, and then manage the job as if it were our own floor. You sign one contract, hold us to one quality standard, and track the whole order in one place, rather than coordinating five suppliers separately.
              </p>
            </motion.div>
          </div>

          <div className="engine-scroll-col">
            <motion.div className="engine-block" initial="hidden" whileInView="visible" viewport={{ once: true, amount: 0.5 }} variants={fadeUp}>
              <h3>Complex Operations, Simplified</h3>
              <p>Short lead times, changing volumes, and wide product ranges are the norm in this industry rather than exceptions. Our merchandisers build the supply chain around your order instead of asking your order to fit a fixed process, selecting the right supplier, the right price, and a delivery window you can plan against.</p>
            </motion.div>

            <motion.div className="engine-block" initial="hidden" whileInView="visible" viewport={{ once: true, amount: 0.5 }} variants={fadeUp}>
              <h3>Independent Quality Control</h3>
              <p>Our quality department does not report to any production manager, so there is never pressure to pass a garment that should fail. Certified auditors run inspections while the order is being made and a final audit before it leaves, and you receive the report either way.</p>
            </motion.div>

            <motion.div className="engine-block" initial="hidden" whileInView="visible" viewport={{ once: true, amount: 0.5 }} variants={fadeUp}>
              <h3>Real-Time Digital Tracking</h3>
              <p>Buyers see the same reporting we do. Online updates keep your team current on every milestone, from the day fabric is booked to the day goods leave the factory. If a date moves, you are notified early rather than discovering it at the port.</p>
            </motion.div>
          </div>
        </div>
      </section>

      {/* ── Asymmetrical Bento Categories ── */}
      <section className="bento-categories-section">
        <div className="container">
          <motion.div className="section-header text-center" initial="hidden" whileInView="visible" viewport={{ once: true }} variants={fadeUp}>
            <h2>Product Capabilities</h2>
            <p className="subtitle">Six categories, one standard. Whether the order is denim, knitwear, or home textiles, it goes through the same development, quality, and reporting process.</p>
          </motion.div>

          <motion.div
            className="bento-grid"
            variants={staggerContainer}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, amount: 0.1 }}
          >
            {categories.map((cat, index) => {
              const count = (catalogue[cat.slug] || []).length;
              return (
                <motion.button
                  type="button"
                  className={`bento-item item-${index}`}
                  key={cat.slug}
                  variants={fadeUp}
                  onClick={() => setOpenCategory(cat)}
                  aria-label={`View the ${cat.title} catalogue`}
                >
                  <img src={cat.image} alt={cat.title} />
                  <div className="bento-overlay">
                    <div className="bento-text">
                      <h3>{cat.title}</h3>
                      <p>{cat.desc}</p>
                      <span className="bento-cta">
                        View catalogue
                        {count > 0 && <span className="bento-count">{count}</span>}
                        <span aria-hidden="true"> &rarr;</span>
                      </span>
                    </div>
                  </div>
                </motion.button>
              );
            })}
          </motion.div>
        </div>
      </section>

      {/* ── Modern High-Contrast CTA ── */}
      <section className="clean-cta">
        <div className="container">
          <motion.div
            className="cta-box"
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            variants={fadeUp}
            style={{ backgroundImage: `url('/img/Production/IMG_3303.jpg')` }}
          >
            <div className="cta-dark-overlay"></div>
            <div className="cta-content-wrapper">
              <h2>One Partner, From Source to Shelf.</h2>
              <a href="/contact" className="cta-link">Start a Sourcing Inquiry &rarr;</a>
            </div>
          </motion.div>
        </div>
      </section>

      {openCategory && (
        <CatalogueModal
          category={openCategory}
          images={catalogue[openCategory.slug] || []}
          onClose={closeModal}
        />
      )}
    </div>
  );
};

export default Sourcing;